import { router } from '@inertiajs/react'
import { Globe, RefreshCw, CheckCircle2, Clock, CircleX, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DnsRecordsTable } from '@/components/settings/DnsRecordsTable'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'
import { cn } from '@/lib/utils'
import type HelpSiteDomain from '@/types/serializers/HelpSiteDomain'

interface HelpSiteDomainSettingsProps {
  domain: HelpSiteDomain | null
  hostedUrl?: string | null
}

export function HelpSiteDomainSettings({ domain, hostedUrl }: HelpSiteDomainSettingsProps) {
  return (
    <div className="space-y-6">
      {hostedUrl && <HostedUrlCard url={hostedUrl} live={!!domain?.live} />}
      {domain ? <ConfiguredState domain={domain} /> : <SetupForm />}
    </div>
  )
}

function HostedUrlCard({ url, live }: { url: string; live: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="size-5" />
          Hosted URL
        </CardTitle>
        <CardDescription>
          {live
            ? 'Your live custom domain is the canonical URL. This hosted URL still works as a fallback.'
            : "This brand's help center is published here. Add a custom domain below to white-label it."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Input readOnly value={url} className="font-mono text-sm" />
          <Button asChild variant="outline" className="cursor-pointer shrink-0">
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="size-4 mr-1" /> Open
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function Title() {
  return (
    <CardTitle className="flex items-center gap-2">
      <Globe className="size-5" />
      Custom domain
    </CardTitle>
  )
}

function SetupForm() {
  const { withAccount } = useRouteParams()
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    router.post(
      routes.helpSiteDomains.create.path(withAccount()),
      { help_site_domain: { domain: value }, ...activeBrandParam() },
      { preserveScroll: true, onFinish: () => setSubmitting(false) },
    )
  }

  return (
    <Card>
      <CardHeader>
        <Title />
        <CardDescription>
          Serve your help center from your own subdomain (e.g. help.acme.com) instead of the hosted Banshee URL.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="help-domain">Domain</Label>
            <Input
              id="help-domain"
              placeholder="help.acme.com"
              value={value}
              onChange={e => setValue(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Use a subdomain such as help.acme.com or support.acme.com. Apex domains and paths are not supported.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={submitting} className="cursor-pointer">
              {submitting ? 'Adding…' : 'Add domain'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function ConfiguredState({ domain }: { domain: HelpSiteDomain }) {
  const { withAccount } = useRouteParams()
  const [checking, setChecking] = useState(false)

  const handleCheck = () => {
    setChecking(true)
    router.post(routes.helpSiteDomains.check.path(withAccount()), { ...activeBrandParam() }, {
      preserveScroll: true,
      onFinish: () => setChecking(false),
    })
  }

  const handleRemove = () => {
    router.delete(routes.helpSiteDomains.destroy.path(withAccount()), { data: activeBrandParam(), preserveScroll: true })
  }

  const hasChecked = !!domain.dns_checked_at
  const ownershipRecords = domain.dns_records.filter(r => r.group === 'ownership' || r.group === 'routing')
  const certRecords = domain.dns_records.filter(r => r.group === 'certificate')

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Title />
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge label="DNS" state={domain.verification_status} pendingText="DNS pending" okText="DNS verified" />
            <StatusBadge label="SSL" state={domain.certificate_status} pendingText="SSL pending" okText="SSL active" okStates={['active']} />
            <StatusBadge label="Route" state={domain.routing_status} pendingText="Route pending" okText="Route live" okStates={['live']} />
            {domain.live && (
              <Badge className="gap-1 bg-emerald-600">
                <CheckCircle2 className="size-3" /> Live
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>{domain.domain}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {domain.live ? (
          <LiveBody domain={domain} />
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Add these DNS records with your domain provider. We keep checking in the background, and your hosted help
              center URL stays active until this domain is fully live.
            </p>

            {domain.failure_reason && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <CircleX className="mt-0.5 size-4 shrink-0 text-destructive" />
                <span>{domain.failure_reason}</span>
              </div>
            )}

            <DnsRecordsTable records={ownershipRecords} label="Required" checked={hasChecked} />
            {certRecords.length > 0 && (
              <DnsRecordsTable records={certRecords} label="Certificate validation" checked={hasChecked} />
            )}

            <p className="text-xs text-muted-foreground">
              DNS changes can take minutes to a few hours to propagate.
              {domain.last_checked_at && <> Last checked {formatElapsed(domain.last_checked_at)}.</>}
            </p>
          </>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button onClick={handleCheck} disabled={checking} variant={domain.live ? 'outline' : 'default'} className="cursor-pointer">
            <RefreshCw className={cn('size-4 mr-2', checking && 'animate-spin')} />
            {checking ? 'Checking…' : 'Check now'}
          </Button>
          <ConfirmDialog
            trigger={<Button variant="destructive" className="cursor-pointer">Remove domain</Button>}
            title="Remove custom domain?"
            description={`This removes ${domain.domain}. Your help center stays available at its hosted Banshee URL.`}
            confirmText="Remove domain"
            onConfirm={handleRemove}
            destructive
          />
        </div>
      </CardContent>
    </Card>
  )
}

function LiveBody({ domain }: { domain: HelpSiteDomain }) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">Your help center is live at:</p>
      <a
        href={`https://${domain.domain}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        https://{domain.domain} <ExternalLink className="size-3.5" />
      </a>
      <p className="text-xs text-muted-foreground">
        Canonical article URLs, sitemap, and sharing metadata now prefer this domain. The hosted Banshee URL remains a
        fallback.
      </p>
    </div>
  )
}

const PENDING_ICON = <Clock className="size-3" />

function StatusBadge({
  label, state, pendingText, okText, okStates = ['success', 'active', 'live'],
}: {
  label: string
  state: string
  pendingText: string
  okText: string
  okStates?: string[]
}) {
  if (state === 'failed') {
    return (
      <Badge variant="outline" className="gap-1 border-destructive/50 text-destructive">
        <CircleX className="size-3" /> {label} failed
      </Badge>
    )
  }
  if (okStates.includes(state)) {
    return (
      <Badge variant="outline" className="gap-1 border-emerald-600/40 text-emerald-600">
        <CheckCircle2 className="size-3" /> {okText}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="gap-1 text-muted-foreground">
      {PENDING_ICON} {pendingText}
    </Badge>
  )
}

function formatElapsed(isoDate: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}
